import React from 'react';
import PropTypes from 'prop-types';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import axios from '../axiosClient.js';
import FormatDollarAmount from './FormatDollarAmount.jsx';
import PackageChips from './PackageChips.jsx';
import TableDate from './TableDate.jsx';

function formatLocalDateTime(iso) {
  if (!iso) {
    return { date: '-', time: '-' };
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return { date: '-', time: '-' };
  }

  return {
    date: d.toLocaleDateString(),
    time: d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
  };
}

function formatLocation(loc) {
  if (!loc) {
    return '-';
  }

  // FedEx scanLocation shape
  const city = loc.city || loc.City || '';
  const state = loc.stateOrProvinceCode || loc.state || loc.State || '';
  const country = loc.countryCode || loc.country || loc.Country || '';
  const parts = [city, state].filter(Boolean).join(', ');
  return [parts, country].filter(Boolean).join(' ');
}

function pickFirstTrackingNumber(trackingIdRaw) {
  const raw = String(trackingIdRaw || '').trim();
  if (!raw) {
    return '';
  }
  // Common delimiters: comma, semicolon, whitespace, pipe
  return raw.split(/[\s,;|]+/).filter(Boolean)[0] || '';
}

// ============================================================================
// TrackingStatusHover
// ----------------------------------------------------------------------------
// Wrap any child UI and show a tooltip that loads tracking status on hover.
// ============================================================================
function TrackingStatusHover({ commission, children }) {
  const trackingNumber = React.useMemo(
    () => pickFirstTrackingNumber(commission.TrackingID),
    [commission.TrackingID]
  );

  const [loading, setLoading] = React.useState(false);
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState(null);

  const fetchOnce = React.useCallback(async () => {
    if (!trackingNumber) {
      return;
    }
    if (loading) {
      return;
    }

    // Already loaded for this row instance
    if (data || error) {
      return;
    }

    setLoading(true);
    try {
      const res = await axios.get('/api/shipping/track', {
        params: { trackingNumber },
      });
      setData(res.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Tracking lookup failed');
    }
    setLoading(false);
  }, [data, error, loading, trackingNumber]);

  const tooltipContent = React.useMemo(() => {
    if (!trackingNumber) {
      return <Typography variant="body2">No tracking number</Typography>;
    }

    if (loading) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="body2">Loading status…</Typography>
        </Box>
      );
    }

    if (error) {
      return (
        <Typography variant="body2" sx={{ maxWidth: 260 }}>
          {error}
        </Typography>
      );
    }

    if (!data?.ok) {
      const msg =
        data?.errorMessage ||
        data?.error ||
        data?.message ||
        (data?.errorCode ? `Tracking unavailable (${data.errorCode})` : null) ||
        'No tracking status available.';

      return (
        <Typography variant="body2" sx={{ maxWidth: 320, whiteSpace: 'pre-wrap' }}>
          {msg}
        </Typography>
      );
    }

    const delivered = Boolean(data.delivered);
    const eta = data.eta || data.etaDate || null;
    const lastLoc = data.lastLocation || null;

    const deliveredAt = data.deliveredAt || null;
    const signedForBy = data.signedForBy || data.signed_by || null;

    const deliveredFmt = formatLocalDateTime(deliveredAt);
    const etaFmt = formatLocalDateTime(eta);

    if (delivered) {
      return (
        <Box sx={{ maxWidth: 320 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Delivered
          </Typography>
          <Typography variant="body2">Date: {deliveredFmt.date}</Typography>
          <Typography variant="body2">Time: {deliveredFmt.time}</Typography>
          <Typography variant="body2">Name: {signedForBy || '-'}</Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ maxWidth: 320 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          In Transit
        </Typography>
        <Typography variant="body2">Est DeliverDate: {eta ? etaFmt.date : '-'}</Typography>
        <Typography variant="body2">Current Location: {formatLocation(lastLoc)}</Typography>
      </Box>
    );
  }, [data, error, loading, trackingNumber]);

  return (
    <Tooltip arrow placement="top" onOpen={fetchOnce} title={tooltipContent}>
      <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>{children}</Box>
    </Tooltip>
  );
}

TrackingStatusHover.propTypes = {
  commission: PropTypes.object.isRequired,
  children: PropTypes.node.isRequired,
};

function CommissionsRow({ commission, isRep }) {
  const tierColor = React.useMemo(() => {
    if (!isRep) {
      return 'paper';
    }

    if (commission.RunningNetTotal < commission.Tier2Start) {
      return 'paper';
    }

    if (commission.RunningNetTotal < commission.Tier3Start) {
      return 'aqua';
    }

    return 'aquamarine';
  }, [commission.RunningNetTotal, commission.Tier2Start, commission.Tier3Start, isRep]);

  const reps = React.useMemo(
    () =>
      [
        commission.Rep,
        commission['2ndRep'],
        commission['3rdRep'],
        commission['4thRep'],
        commission['5thRep'],
      ].filter(Boolean),
    [commission]
  );

  return (
    <TableRow>
      <TableCell data-cy={`invoice-number-${commission.InvoiceNo}`}>
        {commission.InvoiceNo}
      </TableCell>
      <TableCell data-cy={`invoice-date-${commission.InvoiceNo}`}>
        <TableDate date={commission.InvoiceDate} />
      </TableCell>
      <TableCell data-cy={`customer-${commission.InvoiceNo}`}>{commission.Customer}</TableCell>
      <TableCell data-cy={`customerpono-${commission.InvoiceNo}`}>
        {commission.CustomerPONo}
      </TableCell>

      {/* Tracking popup — hover to load delivery status (UPS / FedEx) */}
      <TableCell data-cy={`trackingids-${commission.InvoiceNo}`}>
        {commission.TrackingID ? (
          <TrackingStatusHover commission={commission}>
            <PackageChips commission={commission} />
          </TrackingStatusHover>
        ) : (
          '-'
        )}
      </TableCell>

      <TableCell data-cy={`state-${commission.InvoiceNo}`}>{commission.State}</TableCell>
      <TableCell data-cy={`areacode-${commission.InvoiceNo}`}>{commission.AreaCode}</TableCell>
      <TableCell data-cy={`city-${commission.InvoiceNo}`}>{commission.City}</TableCell>
      <TableCell data-cy={`reps-${commission.InvoiceNo}`} sx={{ maxWidth: 200 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '0.25em' }}>
          {reps.map((rep) => (
            <Chip
              key={rep}
              label={rep}
              data-cy={`rep-${rep}-${commission.InvoiceNo}`}
              sx={{ margin: '0.125rem', fontSize: '0.75rem', padding: '0.25em' }}
            />
          ))}
        </Box>
      </TableCell>
      <TableCell data-cy={`item-${commission.InvoiceNo}`}>{commission.Item}</TableCell>
      <TableCell data-cy={`productgroup-${commission.InvoiceNo}`}>
        {commission.ProductGroup}
      </TableCell>
      <TableCell data-cy={`quantity-${commission.InvoiceNo}`}>{commission.Quantity}</TableCell>
      <TableCell data-cy={`unitprice-${commission.InvoiceNo}`}>
        <FormatDollarAmount amount={commission.UnitPrice} />
      </TableCell>
      <TableCell data-cy={`sale-${commission.InvoiceNo}`}>
        <FormatDollarAmount amount={commission.Sale} />
      </TableCell>
      <TableCell data-cy={`basecost-${commission.InvoiceNo}`}>
        <FormatDollarAmount amount={commission.BaseCost} />
      </TableCell>
      <TableCell data-cy={`contractfee-${commission.InvoiceNo}`}>
        <FormatDollarAmount amount={commission.ContractFee} />
      </TableCell>
      <TableCell data-cy={`netsale-${commission.InvoiceNo}`}>
        <FormatDollarAmount amount={commission.NetSale} />
      </TableCell>
      <TableCell data-cy={`runningtotal-${commission.InvoiceNo}`}>
        <FormatDollarAmount amount={commission.RunningTotal} />
      </TableCell>
      <TableCell data-cy={`runningnettotal-${commission.InvoiceNo}`}>
        <FormatDollarAmount amount={commission.RunningNetTotal} />
      </TableCell>
      {isRep && (
        <>
          <TableCell
            data-cy={`triggeredtiers-${commission.InvoiceNo}`}
            variant="head"
            sx={(theme) => ({
              backgroundColor: tierColor === 'paper' ? theme.palette.background.paper : tierColor,
              color: tierColor !== 'paper' ? theme.palette.common.black : undefined,
            })}
          >
            {commission.TriggeredTiers || '-'}
          </TableCell>
          <TableCell data-cy={`linecommission-${commission.InvoiceNo}`} variant="head">
            {commission.LineCommissions === null || commission.LineCommissions === undefined ? (
              '-'
            ) : (
              <FormatDollarAmount amount={commission.LineCommissions} />
            )}
          </TableCell>
          <TableCell data-cy={`runningcommissiontotal-${commission.InvoiceNo}`} variant="head">
            {commission.RunningCommissionsTotal === null ||
            commission.RunningCommissionsTotal === undefined ? (
              '-'
            ) : (
              <FormatDollarAmount amount={commission.RunningCommissionsTotal} />
            )}
          </TableCell>
        </>
      )}
    </TableRow>
  );
}

CommissionsRow.propTypes = {
  commission: PropTypes.shape({
    InvoiceNo: PropTypes.string.isRequired,
    InvoiceDate: PropTypes.string.isRequired,
    Customer: PropTypes.string.isRequired,
    CustomerPONo: PropTypes.string,
    TrackingID: PropTypes.string,
    State: PropTypes.string.isRequired,
    AreaCode: PropTypes.string.isRequired,
    City: PropTypes.string.isRequired,
    Rep: PropTypes.string.isRequired,
    '2ndRep': PropTypes.string,
    '3rdRep': PropTypes.string,
    '4thRep': PropTypes.string,
    '5thRep': PropTypes.string,
    Item: PropTypes.string.isRequired,
    ProductGroup: PropTypes.string.isRequired,
    Quantity: PropTypes.number.isRequired,
    UnitPrice: PropTypes.number.isRequired,
    Sale: PropTypes.number.isRequired,
    BaseCost: PropTypes.number.isRequired,
    ContractFee: PropTypes.number.isRequired,
    NetSale: PropTypes.number.isRequired,
    RunningTotal: PropTypes.number.isRequired,
    RunningNetTotal: PropTypes.number.isRequired,
    Tier2Start: PropTypes.number,
    Tier3Start: PropTypes.number,
    TriggeredTiers: PropTypes.string,
    LineCommissions: PropTypes.number,
    RunningCommissionsTotal: PropTypes.number,
  }).isRequired,
  isRep: PropTypes.bool.isRequired,
};

export default React.memo(CommissionsRow);
